import { z } from 'zod';

import { CHANNEL_BUCKETS } from './channel-buckets';
import type { ChannelBucket } from './channel-buckets';

/**
 * What the tasks list narrows by, and the only definition of it.
 *
 * Declared here rather than in `route.tsx` so the type can be *inferred* rather
 * than read back out of the router registry. `ReturnType<typeof
 * routeApi.useSearch>` looks equivalent and is not: resolving it means
 * resolving the route, which imports the panel, which imports this — and
 * TypeScript settles that cycle by calling the whole thing `any`. Every read of
 * `tab`, `q`, `important` and `unread` was unchecked until this moved.
 *
 * The tabs are the primary control; the rest narrow within the tab. A filter
 * that is off means "either", not "false".
 */
export const defaultTasksSearch = {
  tab: 'open' as const,
  q: '',
  important: false,
  unread: false,
  channels: [] as Array<ChannelBucket>,
  assignees: [] as Array<string | null>,
};

export const tasksSearchSchema = z.object({
  tab: z
    .enum(['open', 'completed'])
    .catch(defaultTasksSearch.tab)
    .default(defaultTasksSearch.tab),
  q: z.string().catch(defaultTasksSearch.q).default(defaultTasksSearch.q),
  important: z.boolean().catch(false).default(false),
  unread: z.boolean().catch(false).default(false),
  /** Empty means every channel. A list nobody has narrowed is not a list of
   *  none — see the note on `channels` in `contacts/inbox.ts`. */
  channels: z.array(z.enum(CHANNEL_BUCKETS)).catch([]).default([]),
  /** `null` is nobody's desk — a pile you can ask for, not the absence of a
   *  choice. Empty is the absence of a choice. */
  assignees: z
    .array(z.union([z.string(), z.null()]))
    .catch([])
    .default([]),
});

export type TasksSearch = z.infer<typeof tasksSearchSchema>;
